import { Badge, IconButton } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import { makeStyles, useTheme, withStyles } from '@material-ui/core/styles';
import UserIcon from '@material-ui/icons/AccountCircle';
import CloseIcon from '@material-ui/icons/Close';
import DeveloperModeIcon from '@material-ui/icons/DeveloperMode';
import HelpIcon from '@material-ui/icons/Help';
import MessageIcon from '@material-ui/icons/Message'; // or 'ChatBubble' for an alternative chat icon
import SettingsIcon from '@material-ui/icons/Settings';
import TabletAndroidIcon from '@material-ui/icons/TabletAndroid';
import React, {useCallback} from 'react';
import { useMemo } from 'react';
import {
	MenuItemLink,
	UserMenu,
	useLocale,
	usePermissions,
	useRedirect,
	useTranslate,
} from 'react-admin';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { httpClient } from '../api/httpClient.js';
import { useSocketContext } from '../contexts/SocketProvider.js';
import { useUserSettingsContext } from '../contexts/UserSettingsProvider';
import { AppManagerRoles, AppRoles, UserRole } from '../entities/';
import { SocketRooms } from '../entities/SocketRooms';
import { isInArray } from '../utils/general';
import { DefaultLng } from '../utils/locale';
import { useNotifyError } from '../utils/notifiers/useNotifyError';
import { ChatToast } from '../components/toasts/ChatToast';
import { DefaultToast } from '../components/toasts/DefaultToast';
import { LocaleSwitcher } from './LocaleSwitcher.js';

const MenuItemClickRaw = withStyles((theme) => ({
	active: {
		color: theme.palette.text.secondary,
	},
}))(MenuItemLink);

export const MenuItemClick = (props) => {
	return <MenuItemClickRaw to="#" {...props} />;
};

const useUserMenuStyles = makeStyles((theme) => ({
	userButton: {
		marginLeft: theme.spacing(1),
	},
}));

const CustomToast = (message) => {
	if (typeof message === 'string') {
		return(<p>{message}</p>)
	}
	return <DefaultToast message={message} />;
};

const handleEventNotification = (eventsPayload) => {
	localStorage.setItem("events", JSON.stringify(eventsPayload));
	window.dispatchEvent(new Event('storage'));
};

const handleUserDataDraftOrders = (payload) => {
	localStorage.setItem("user_data_draft_orders", JSON.stringify(payload));
	window.dispatchEvent(new Event('storage'));
};

const handleUserDataDeviations = (payload) => {
	localStorage.setItem("user_data_deviations", JSON.stringify(payload));
	window.dispatchEvent(new Event('storage'));
};

const CloseButton = ({ closeToast }) => (
	<IconButton
		aria-label="close"
		size="small"
		onClick={(event) => {
			event.stopPropagation();
			closeToast();
		}}
		style={{ color: "inherit", alignSelf: "flex-start" }}
	>
		<CloseIcon fontSize="small" />
	</IconButton>
);

export const MyUserMenu = (props) => {
	const theme = useTheme();
	const location = useLocation();
	const redirect = useRedirect();
	const translate = useTranslate();
	const notifyError = useNotifyError();
	const { permissions } = usePermissions();
	const userMenuStyles = useUserMenuStyles();
	const { data: userSettings } = useUserSettingsContext();
	const { socket, error: socketError, joinRoom } = useSocketContext();
	
	const [manuals, setManuals] = React.useState([]);
	const [unreadCount, setUnreadCount] = React.useState(0);
	const [currentManual, setCurrentManual] = React.useState(null);
	
	// Setting is stored in seconds; react-toastify autoClose expects ms.
	const toastDurationSec = userSettings?.popup_notification_duration ?? 60 * 5; // 5 min default
	const toastDurationMs = toastDurationSec * 1000;
	// Ref so socket handlers always read the current value without re-registering on every setting change.
	const toastDurationMsRef = React.useRef(toastDurationMs);
	
	const getBaseToastOptions = (message) => ({
		position: "bottom-right",
		closeOnClick: false,
		autoClose: toastDurationMsRef.current,
		pauseOnFocusLoss: false,
		closeButton: CloseButton,
		onClick: () => message?.link && redirect(message.link),
	});
	
	const chatToastStyle = {
		background: theme.palette.success.main,
		color: theme.palette.common.white,
		borderRadius: theme.spacing(1),
		boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
		alignItems: 'flex-start',
	};
	
	React.useEffect(() => {
		toastDurationMsRef.current = toastDurationMs;
	}, [toastDurationMs]);

	React.useEffect(() => {
		if (socket) {
			socket.on('notification', (message) =>
				toast.info(CustomToast(message), getBaseToastOptions(message))
			);
			socket.on('chat_notification', (message) => toast(
				<ChatToast message={message} />, {
				...getBaseToastOptions(message),
				style: chatToastStyle,
				progressStyle: { background: '#bebebe' },
			}));
			socket.on('event_notification', handleEventNotification);
			socket.on('user_data_draft_orders', handleUserDataDraftOrders);
			socket.on('user_data_deviations', handleUserDataDeviations);
			return () => {
				socket.off('notification');
				socket.off('chat_notification');
				socket.off('event_notification');
				socket.off('user_data_draft_orders');
				socket.off('user_data_deviations');
			};
		}
	}, [socket]);


	const fetchMessages = () => {
		// httpClient(`messages?filter=${encodeURIComponent(JSON.stringify({ unread: true }))}`, {
		httpClient(`messages-unread-count`, {
			method: 'GET',
		}).then((response) => {
			setUnreadCount(Number(response.body));
		});
	};

	React.useEffect(() => {
		if (permissions && isInArray(AppManagerRoles, permissions.role)) {
			fetchMessages();
			const intervalId = setInterval(fetchMessages, 60 * 1000);
			return () => clearInterval(intervalId);
		}
		const fetchManual = async () => {
            try {
                const response = await httpClient('user-manuals', {
                    method: 'GET',
                });
                setManuals(JSON.parse(response.body));
            } catch (error) {
                console.error('Failed to fetch user manual:', error);
            }
        };
        fetchManual();
	}, [permissions]);

	// React.useEffect(() => {
	// 	const intervalId = setInterval(draftOrderUserDataFetch, 5000);
	// 	return () => clearInterval(intervalId);
	// }, []);

	React.useEffect(() => {
		if (manuals && permissions) {
			const currentPath = location.pathname.startsWith('/') ? location.pathname.slice(1) : location.pathname;

			const foundManuals = manuals.filter(x => x.page_url === currentPath);
			setCurrentManual(foundManuals.find(x => x.locale === permissions.locale) || foundManuals.find(x => x.locale === DefaultLng));
		}		
    }, [location, manuals, permissions]);

	const handleMessagesClick = useCallback(() => {
		setUnreadCount(0);
		redirect('/messages');
	}, []);

	const handleManualClick = useCallback(() => {
		if (currentManual)
		window.open(currentManual.preview_src, '_blank');
	}, [currentManual]);

	if (!permissions) return null;

	return (
		<React.Fragment>
			{isInArray(AppManagerRoles, permissions.role) ? (
				<IconButton
					// to="/messages"
					// component={Link}
					onClick={handleMessagesClick}					
					style={{ color: 'white' }}
				>
					{unreadCount ? (
						<Badge badgeContent={unreadCount} color="error">
							<MessageIcon />
						</Badge>
					) : (
						<MessageIcon />
					)}
				</IconButton>
			) : null}
			<LocaleSwitcher />
			<Box ml={1}>
				<IconButton
					size="medium"
					onClick={handleManualClick}
					color="inherit"
					disabled={!currentManual}
				>
					{<HelpIcon />}
				</IconButton>
			</Box>
			<UserMenu {...props} classes={userMenuStyles}>
				<MenuItemLink
					to="#"
					sidebarIsOpen
					leftIcon={<UserIcon />}
					primaryText={permissions.email}
				/>
				<MenuItemLink
					to="/settings"
					primaryText={translate('user-menu.settings')}
					leftIcon={<SettingsIcon />}
				/>
				{isInArray([UserRole.osfc_employee, UserRole.osfc_manager], permissions.role) ? (
					<MenuItemLink
						to="/mobile-app"
						primaryText={translate('pages.mobile-app.name')}
						leftIcon={<TabletAndroidIcon />}
					/>
				) : null}
				<Divider />
				{permissions.role === UserRole.admin ? (
					<MenuItemLink
						to="/developer"
						primaryText={translate('user-menu.developer')}
						leftIcon={<DeveloperModeIcon />}
					/>
				) : null}
			</UserMenu>
		</React.Fragment>
	);
};
